import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { useMediaQuery } from '@mui/material';

import { useLocalStorage } from 'src/hooks/use-local-storage';

import { localStorageGetItem } from 'src/utils/storage-available';

import { SettingsContext } from './settings-context';

// ----------------------------------------------------------------------

const STORAGE_KEY = 'settings';

export function SettingsProvider({ children, defaultSettings }) {
  const { state, update, reset } = useLocalStorage(
    STORAGE_KEY,
    defaultSettings
  );

  const systemDark = useMediaQuery('(prefers-color-scheme: dark)');
  const systemMode = systemDark ? 'dark' : 'light';
  const themeMode = state.themeMode === 'system' ? systemMode : state.themeMode;

  const [openDrawer, setOpenDrawer] = useState(false);
  const [layoutOverride, setLayoutOverride] = useState(null);
  const wideDesktop = useMediaQuery('(min-width: 1440px)');

  // Old settings also store the default "vertical" layout. Compact it on
  // narrow screens, while retaining an explicit choice for this session.
  const themeLayout = layoutOverride ?? (
    state.themeLayout === 'vertical' && !wideDesktop ? 'mini' : state.themeLayout
  );

  const onUpdate = useCallback((name, value) => {
    if (name === 'themeLayout') setLayoutOverride(value);
    update(name, value);
  }, [update]);

  const onReset = useCallback(() => {
    setLayoutOverride(null);
    reset();
  }, [reset]);

  const isArabic = localStorageGetItem('i18nextLng') === 'ar';

  useEffect(() => {
    if (isArabic) {
      onChangeDirectionByLang('ar');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isArabic]);

  // Direction by lang
  const onChangeDirectionByLang = useCallback(
    (lang) => {
      update('themeDirection', lang === 'ar' ? 'rtl' : 'ltr');
    },
    [update]
  );

  // Drawer
  const onToggleDrawer = useCallback(() => {
    setOpenDrawer((prev) => !prev);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setOpenDrawer(false);
  }, []);

  const canReset = !isEqual(state, defaultSettings);

  const memoizedValue = useMemo(
    () => ({
      ...state,
      themeModePreference: state.themeMode,
      themeMode,
      themeLayout,
      onUpdate,
      // Direction
      onChangeDirectionByLang,
      // Reset
      canReset,
      onReset,
      // Drawer
      open: openDrawer,
      onToggle: onToggleDrawer,
      onClose: onCloseDrawer,
    }),
    [
      onReset,
      onUpdate,
      themeLayout,
      state,
      themeMode,
      canReset,
      openDrawer,
      onCloseDrawer,
      onToggleDrawer,
      onChangeDirectionByLang,
    ]
  );

  return (
    <SettingsContext.Provider value={memoizedValue}>
      {children}
    </SettingsContext.Provider>
  );
}

SettingsProvider.propTypes = {
  children: PropTypes.node,
  defaultSettings: PropTypes.object,
};
